// ** Modules
import React from 'react';

// ** Components
import ComponentBlocksHero from '@/components/strapi/Blocks/ComponentBlocksHero';
import ComponentBlocksTextBlock from '@/components/strapi/Blocks/ComponentBlocksTextBlock';
import ComponentBlocksCarouselBlock from '@/components/strapi/Blocks/ComponentBlocksCarouselBlock';
import ComponentBlocksBildTextBlock from '@/components/strapi/Blocks/ComponentBlocksBildTextBlock';
import ComponentBlocksButtonLinkRow from '@/components/strapi/Blocks/ComponentBlocksButtonLinkRow';
import ComponentBlocksGridNavigation from '@/components/strapi/Blocks/ComponentBlocksGridNavigation';

// ** Types
import type { User } from 'next-auth';
import type { Provider } from 'next-auth/providers';
import type { AppContext } from '@/types/contextTypes';
import type { Query } from '@/types/graphql';

type PageTitle = NonNullable<Query['homepage']>;
type Hero = NonNullable<Query['homepage']>['homepageHero'];
type DynamicContent = NonNullable<NonNullable<Query['homepage']>['homepageContent']>[number];
type RenderableBlock = PageTitle | Hero | DynamicContent;

const componentsMap = {
	ComponentBlocksHero,
	ComponentBlocksTextBlock,
	ComponentBlocksBildTextBlock,
	ComponentBlocksButtonLinkRow,
	ComponentBlocksCarouselBlock,
	ComponentBlocksGridNavigation
} as const;

type ComponentName = keyof typeof componentsMap;
type HomepageRendererProps = {
	content: Query['homepage'];
	locale: string;
	providers: Provider;
	currentUser: User;
	host: AppContext['host'];
	newsBasePath?: string;
	eventsBasePath?: string;
};

const NO_HERO_SPACING = 20;
/** Top padding for the first block after a hero section (responsive). */
const AFTER_HERO_SPACING = { xs: 10, md: 20 };
/** Bottom padding before the site footer (theme spacing units → 80px). */
const FOOTER_ABOVE_GAP_SPACING = 20;

/**
 * Vertical spacing config for each Strapi block.
 *
 * Rule: blocks that pass a `backgroundColor` to `Section` own their internal
 * padding — their `containerSx` here is applied as-is inside the coloured
 * background. All other blocks receive symmetric `py` from the `default` entry.
 * Never hard-code `py` inside those block components; keep the single source of
 * truth here.
 */
type BlockConfig = { containerSx: Record<string, unknown> };
const COMPONENT_CONFIG: Record<string, BlockConfig> = {
	// Background blocks — responsive padding inside their own colour
	ComponentBlocksCarouselBlock: {
		containerSx: { pt: { xs: 4, md: 20 }, pb: { xs: 10, md: 20 } }
	},
	ComponentBlocksButtonLinkRow: {
		containerSx: { py: { xs: 8, md: 20 } }
	},
	// Special / no-spacing blocks
	ComponentBlocksHero: { containerSx: { py: 0 } },
	// Default symmetric spacing for all other blocks
	default: { containerSx: { py: 10 } }
};

const HomepageRenderer = ({
	content,
	locale,
	providers,
	currentUser,
	host,
	newsBasePath,
	eventsBasePath
}: HomepageRendererProps) => {
	// Define the slug for the homepage
	const slug = '/';

	let newContentArray: RenderableBlock[] = [];

	// Combine all potential blocks
	if (content?.homepageHero) {
		newContentArray.push({
			...content.homepageHero,
			__typename: 'ComponentBlocksHero'
		});
	}

	if (content?.homepageContent) {
		const filteredBlocks = content.homepageContent.filter(
			(block): block is DynamicContent => block !== null && block !== undefined && block.__typename !== 'Error'
		);
		newContentArray = [...newContentArray, ...filteredBlocks];
	}

	return (
		<React.Fragment>
			{newContentArray.map((item, index) => {
				if (!item?.__typename) return null;

				const typeName = item.__typename as ComponentName;
				const Component = componentsMap[typeName] as React.ElementType;
				if (!Component) return null;

				const config = COMPONENT_CONFIG[typeName] || COMPONENT_CONFIG.default;

				// Spacing Logic
				const isFirst = index === 0;
				const isLast = index === newContentArray.length - 1;
				const isHero = typeName === 'ComponentBlocksHero';
				const isFirstAfterHero = index === 1 && newContentArray[0]?.__typename === 'ComponentBlocksHero';

				const containerSx = {
					...config.containerSx,
					...(isFirst && !isHero && { pt: NO_HERO_SPACING }),
					...(isHero && { py: 0 }),
					...(isFirstAfterHero && { pt: AFTER_HERO_SPACING }),
					...(isLast && { pb: FOOTER_ABOVE_GAP_SPACING })
				};
				const itemKey = `${typeName}-${'id' in item ? item.id : index}-${locale}`;

				return (
					<Component
						key={itemKey}
						locale={locale}
						providers={providers}
						currentUser={currentUser}
						host={host}
						slug={slug}
						containerSx={containerSx}
						extraProps={{ newsBasePath, eventsBasePath }}
						{...item}
					/>
				);
			})}
		</React.Fragment>
	);
};

export default HomepageRenderer;
